import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

type Mode = "signin" | "signup";

export default function AdminLogin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  if (!loading && user) {
    return <Navigate to="/admin" replace />;
  }

  const switchMode = (next: Mode) => {
    setMode(next);
    setError("");
    setNotice("");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setNotice("");

    try {
      if (mode === "signin") {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (signInError) throw signInError;
        navigate("/admin", { replace: true });
      } else {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email: email.trim(),
          password,
        });
        if (signUpError) throw signUpError;
        if (data.session) {
          navigate("/admin", { replace: true });
        } else {
          setNotice(
            "Account created. Please confirm your email address, then sign in below.",
          );
          setMode("signin");
        }
      }
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400";
  const labelClass = "block text-sm font-medium text-foreground-800 mb-2";

  return (
    <div className="min-h-screen bg-background-100 grid grid-cols-1 lg:grid-cols-2">
      <meta name="robots" content="noindex, nofollow" />
      <div className="relative hidden lg:flex flex-col justify-between sun-glaze bg-primary-900 p-10 xl:p-14 text-background-50">
        <BrandLogo imgClassName="h-12" />
        <div>
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
            <i className="ri-calendar-check-line text-2xl"></i>
          </span>
          <h1 className="font-heading text-4xl xl:text-5xl font-semibold leading-tight mt-6 max-w-md">
            Every booking, one quiet dashboard.
          </h1>
          <p className="text-background-200 leading-relaxed mt-5 max-w-md">
            See new experience requests and custom itineraries the moment they arrive, confirm
            them, add notes and keep your team in the loop — all in one place.
          </p>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-background-200">
          {["Real-time bookings", "Itineraries attached", "Team notes"].map((item) => (
            <span key={item} className="inline-flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-accent-400"></i>
              {item}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-12 md:px-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <BrandLogo imgClassName="h-11" />
          </div>

          <div className="bg-background-50 border border-background-200 rounded-lg p-7 md:p-9">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-shield-user-line text-2xl"></i>
            </span>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mt-5">
              {mode === "signin" ? "Team sign in" : "Create team account"}
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mt-2">
              {mode === "signin"
                ? "Sign in to manage bookings and itineraries."
                : "Set up an account to access the bookings dashboard."}
            </p>

            <form onSubmit={handleSubmit} className="mt-7 space-y-4">
              <div>
                <label htmlFor="admin-email" className={labelClass}>
                  Email
                </label>
                <input
                  id="admin-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="admin-password" className={labelClass}>
                  Password
                </label>
                <input
                  id="admin-password"
                  name="password"
                  type="password"
                  required
                  minLength={6}
                  autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="At least 6 characters"
                  className={inputClass}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
                {!submitting && (
                  <i className={mode === "signin" ? "ri-login-box-line" : "ri-user-add-line"}></i>
                )}
              </button>
            </form>

            {notice ? (
              <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-100 border border-primary-200 rounded-md px-4 py-3 mt-4">
                <i className="ri-mail-check-line text-lg"></i>
                <span>{notice}</span>
              </p>
            ) : null}
            {error ? (
              <p className="flex items-start gap-2 text-sm text-accent-900 bg-accent-100 border border-accent-300 rounded-md px-4 py-3 mt-4">
                <i className="ri-error-warning-line text-lg"></i>
                <span>{error}</span>
              </p>
            ) : null}

            <div className="mt-6 pt-5 border-t border-background-200 text-sm text-foreground-600">
              {mode === "signin" ? (
                <p>
                  Need an account?{" "}
                  <button
                    type="button"
                    onClick={() => switchMode("signup")}
                    className="font-semibold text-primary-700 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    Create one
                  </button>
                </p>
              ) : (
                <p>
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => switchMode("signin")}
                    className="font-semibold text-primary-700 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    Sign in
                  </button>
                </p>
              )}
            </div>
          </div>

          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors mt-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}